/**
 * The first-run checklist itself: five rows, three treatments, one obvious next
 * move.
 *
 * It takes the `in_progress` arm of `GET /onboarding` as props and fetches
 * nothing. That split is the point rather than tidiness — `OnboardingPanel` owns
 * the request and the three async states, this file owns the words and the
 * markup, and only this half can be rendered by `scripts/verify-onboarding-panel.tsx`.
 * Anything importing `lib/api/client` drags in `lib/config`, which reads
 * `import.meta.env` and only evaluates inside Vite; slice 13 hit that wall and
 * answered it the same way, by keeping the claims in a component that imports no
 * client. The copy on this screen *is* the deliverable, so the copy has to be
 * the part a script can assert against.
 *
 * ## Three states, three treatments, and none of them is a greyed row
 *
 *   done      a filled tick, the label in `text-muted`, nothing else on the row.
 *             There is nothing to say about a step that is finished, and a
 *             "Done" chip on every completed row is five chips saying what five
 *             ticks already said.
 *   ready     the label at full contrast and exactly one action, linking to the
 *             route that actually performs it. The step the response names as
 *             `next` gets the primary button and every other ready step gets a
 *             secondary one, so a workspace with three unblocked steps still has
 *             one thing to do first.
 *   blocked   a dashed ring, no action at all, and **a sentence naming what is
 *             missing** — "Needs Amazon SES and an email template."
 *
 * **The sentence on a blocked row is the reason this component exists in this
 * shape.** A step that is merely dimmed is a step somebody clicks at, gets
 * nothing from, and files as a bug; the brief for this slice names that outcome
 * and the shared contract refuses to let the two states collapse — there is no
 * boolean in `OnboardingStep`, so `!done` is not computable and a component
 * cannot accidentally render blocked and ready the same way. The list of what is
 * missing comes from the response rather than from a rule restated here, and its
 * wording comes from the same map the labels do, so the row that fixes a block
 * and the block that names it cannot call the same thing two different things.
 *
 * ## Colour is never the only carrier
 *
 * The three marks are three different silhouettes — tick, ring, dashed ring —
 * not one glyph in three colours, the same discipline `Callout` applies to its
 * tones. On top of that each row opens with a visually hidden status word, so a
 * screen reader hears "Done: Connect Amazon SES" rather than a bare label whose
 * state was carried entirely by a decorative SVG. The `next` row carries
 * `aria-current="step"`, which is what that attribute is for.
 *
 * ## What is deliberately not here
 *
 * **No progress count.** "2 of 5" is a number that changes no decision — five
 * ticks already say it — and this slice is the one that takes page-filling out
 * of the product rather than adding a new source of it.
 *
 * **No dismiss control, and no way for a person to tick anything.** Every row is
 * derived from state that is true right now, so a workspace that disconnects its
 * SES account correctly goes back to not-ready. The stored-checklist alternative
 * is argued down in the contract's header: a flag drifts from reality on the
 * first undo, and then the panel is lying about the one thing it exists to say.
 * `Each step ticks itself` is on the card for that reason — four words that stop
 * somebody hunting for a control that does not exist.
 *
 * **No explanation of why the order is the order.** That reasoning is in the
 * contract's header and in `SLICES.md`, which is where the text rule for this
 * slice sends it. The rows are imperative and short; a paragraph above them
 * explaining the theory of onboarding would be the exact prose this slice is
 * removing from every other screen.
 */
import { type OnboardingStep, type OnboardingStepId } from '@wend/shared';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  buttonVariants,
  cn,
} from '@wend/ui';
import { Circle, CircleCheck, CircleDashed } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ONBOARDING_STEP_DISPLAY, blockedSentence } from './onboarding-display';

/**
 * Per state: the silhouette, its colour, and the word a screen reader hears in
 * place of both. Keyed by the union's own discriminant, so a fourth state added
 * to the contract fails to compile here rather than rendering an invisible row.
 */
interface StepMark {
  readonly Icon: typeof Circle;
  readonly tone: string;
  readonly word: string;
}

const MARKS: Readonly<Record<OnboardingStep['status'], StepMark>> = {
  done: { Icon: CircleCheck, tone: 'text-good', word: 'Done' },
  ready: { Icon: Circle, tone: 'text-muted', word: 'To do' },
  blocked: { Icon: CircleDashed, tone: 'text-muted', word: 'Blocked' },
};

function OnboardingRow({ step, isNext }: { step: OnboardingStep; isNext: boolean }) {
  const display = ONBOARDING_STEP_DISPLAY[step.id];
  const mark = MARKS[step.status];

  return (
    <li
      aria-current={isNext ? 'step' : undefined}
      className="flex items-start gap-3 border-t border-line px-[18px] py-4 first:border-t-0"
    >
      <mark.Icon aria-hidden="true" className={cn('mt-0.5 size-4 shrink-0', mark.tone)} />

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p
          className={cn(
            'text-[0.92rem] leading-snug',
            step.status === 'done' ? 'text-muted' : 'text-text',
          )}
        >
          <span className="sr-only">{mark.word}: </span>
          {display.label}
        </p>

        {step.status === 'blocked' && (
          <p className="text-[0.84rem] leading-snug text-muted">
            {blockedSentence(step.blockedBy)}
          </p>
        )}
      </div>

      {step.status === 'ready' && (
        <Link
          to={display.action.href}
          className={cn(
            buttonVariants({ size: 'sm', variant: isNext ? 'primary' : 'secondary' }),
            'shrink-0',
          )}
        >
          {display.action.label}
        </Link>
      )}
    </li>
  );
}

export interface OnboardingChecklistProps {
  /** Every step, in the contract's order — done ones included. */
  readonly steps: readonly OnboardingStep[];
  /**
   * The first step that is not done. Always a `ready` one — the contract's own
   * parse refuses a response where it is not.
   */
  readonly next: OnboardingStepId;
  className?: string;
}

/**
 * The panel as a whole. It never renders itself away — a checklist with nothing
 * left to do cannot be built out of the `in_progress` arm, and the arm choice is
 * `OnboardingPanel`'s.
 */
export function OnboardingChecklist({ steps, next, className }: OnboardingChecklistProps) {
  return (
    <Card className={className}>
      <CardHeader className="p-[18px] pb-3.5">
        <CardTitle className="text-[0.95rem]">Five steps to your first campaign</CardTitle>
        <CardDescription>Each step ticks itself.</CardDescription>
      </CardHeader>

      <CardContent className="p-0">
        <ol className="flex flex-col">
          {steps.map((step) => (
            <OnboardingRow key={step.id} step={step} isNext={step.id === next} />
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
